import { Component, OnDestroy } from '@angular/core';

import { BehaviorSubject, combineLatest, map, Observable, of, Subject, switchMap } from 'rxjs';

import { ApiChangeChapterRequest, ApiChapterItem, ApiColor, ApiPaperListItem, ChaptersClient, ColorsClient, PaperNotesClient, PapersClient } from 'content-manager/src/app/core/open-api/content-manager-api-client';
import { ProjectPapersRenderers } from 'content-manager/src/app/projects/project/papers/project-papers-renderers';
import { ProjectStateService } from 'content-manager/src/app/projects/project/project-state.service';
import { ApiNote, Filters, NoteTypeEnum, PageResult, Renderers, TableSettings } from 'sc-common';
import { ICommentsData } from 'sc-common/shared/components/comments-dialog/comments-dialog-data';
import { ExternalRenderers } from 'sc-external/shared/table/renderers/renderers';

@Component({
    templateUrl: './project-papers.component.html',
    standalone: false
})
export class ProjectPapersComponent implements OnDestroy {

    public readonly tableSettings$: Observable<TableSettings<ApiPaperListItem>>;

    public unassignedPapersCount: number;

    public filteredByAuthor$: Observable<string>;

    private readonly _memberId$ = new BehaviorSubject<number | null>(null);

    private readonly _refreshTable$ = new Subject<any>();

    private readonly _destroy$ = new Subject<void>();

    private readonly _blankChapterItem = { id: null, name: $localize`blank` } as ApiChapterItem;

    private readonly _rejectChapterItem = { id: -1, name: $localize`Rejected` } as ApiChapterItem;

    private _lastOrder: Map<number, number> | null = null;

    constructor(
        private readonly _projectStateService: ProjectStateService,
        private readonly _papersClient: PapersClient,
        private readonly _chaptersClient: ChaptersClient,
        private readonly _paperNotesClient: PaperNotesClient,
        private readonly _colorsClient: ColorsClient) {

        this.tableSettings$ = _projectStateService.model$.pipe(map(project => this._initTable(project.isReadonly)));
        this.filteredByAuthor$ = combineLatest([this._projectStateService.model$, this._memberId$])
            .pipe(
                switchMap(([project, memberId]) => memberId ? this._papersClient.getAuthor(project.id, memberId) : of(null)),
                map(author => author?.fullName));
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._refreshTable$.complete();
        this._memberId$.complete();
    }

    public clearFilterByAuthor(event: Event): void {
        event.preventDefault();
        this._memberId$.next(null);
    }

    private _preserveOrder(items: ApiPaperListItem[]): ApiPaperListItem[] {
        if (this._lastOrder == null) {
            this._lastOrder = new Map(items.map((item, index) => [item.id, index]));
            return items;
        }

        const order = this._lastOrder;
        const fallback = order.size;
        const sorted = [...items].sort((a, b) => {
            const ai = order.has(a.id) ? order.get(a.id) : fallback;
            const bi = order.has(b.id) ? order.get(b.id) : fallback;
            return ai - bi;
        });
        this._lastOrder = new Map(sorted.map((item, index) => [item.id, index]));
        return sorted;
    }

    private _refreshRow(rowData: ApiPaperListItem): Observable<void> {
        return this._projectStateService.firstSwitchMap(project =>
            this._papersClient.getList(project.id, this._memberId$.value, `?$filter=id eq ${ rowData.id }`)
                .pipe(map(pageResult => {
                    const fresh = pageResult.items?.[0];
                    if (!fresh) {
                        return;
                    }
                    const rowNumber = rowData.rowNumber;
                    Object.assign(rowData, fresh);
                    rowData.rowNumber = rowNumber;
                    if (rowData.isRejected && rowData.chapter == null) {
                        rowData.chapter = this._rejectChapterItem;
                    }
                })));
    }

    private _initTable(isReadonly: boolean): TableSettings<ApiPaperListItem> {
        const chapterList$ = this._projectStateService.switchMap(project => this._chaptersClient.getAll(project.id));
        const colorList$ = this._colorsClient.list();
        const chapterListForDropdown$ = chapterList$.pipe(map(chapters => [this._blankChapterItem, this._rejectChapterItem, ...chapters]));

        return TableSettings.create(ApiPaperListItem, {
            dataSource: oDataQuery => this._getDataSource(oDataQuery),
            columnResizeMode: 'fit',
            paginator: false,
            hasFilters: true,
            showDiscardButton: false,
            emptyMessageText: $localize`No papers`,
            reload$: this._refreshTable$.asObservable(),
            rowStyler: item => item.color != null ? { 'background-color': item.color.value } : null,
            columns: {
                rowNumber: {
                    header: $localize`#`,
                    sortable: false,
                    filterable: false,
                    width: '40px'
                },
                name: {
                    header: $localize`Paper Name`,
                    renderer: Renderers.rawHtml(),
                    sortable: false,
                    wrap: true
                },
                isEditorial: {
                    hidden: true
                },
                pdf: {
                    header: $localize`PDF`,
                    renderer: ExternalRenderers.fileDownload((model: ApiPaperListItem) =>
                        this._projectStateService.firstSwitchMap((project => this._papersClient.getPdf(project.id, model.id)))),
                    width: '60px',
                    sortable: false,
                    filterable: false
                },
                chapter: {
                    header: $localize`Chapters`,
                    wrap: true,
                    renderer: isReadonly
                        ? null
                        : ExternalRenderers.dropdown(chapterListForDropdown$, (x: ApiChapterItem) => x?.name, 'id', 'name', null, false, $localize`Choose chapter`),
                    hideCellIf: (rowData: ApiPaperListItem) => rowData.isEditorial,
                    editable: isReadonly
                        ? null
                        : {
                            callback: (cellData, rowData) => this._projectStateService.firstSwitchMap(project => {
                                if (cellData?.id === this._rejectChapterItem.id) {
                                    return this._papersClient.changeChapter(project.id, rowData.id, new ApiChangeChapterRequest({ chapterId: null, reject: true }));
                                } else {
                                    return this._papersClient.changeChapter(project.id, rowData.id, new ApiChangeChapterRequest({ chapterId: cellData.id, reject: false }));
                                }
                            }).pipe(switchMap(() => this._refreshRow(rowData))),
                            reloadRequired: false
                        },
                    sortable: false,
                    useBlankFilterItem: true,
                    filter: Filters.multiselect(chapterList$.pipe(map(x => x.map(k => ({ id: k.id, name: k.name }))))),
                    filterExpr: x => x?.id,
                    displayExpr: x => x?.name
                },
                titleName: {
                    header: $localize`Book`,
                    sortable: false,
                    wrap: true
                },
                isRejected: {
                    hidden: true
                },
                authorCounts: {
                    header: $localize`Authors`,
                    renderer: ProjectPapersRenderers.authors(memberId => this._memberId$.next(memberId)),
                    filterExpr: authorCount => authorCount.fullName,
                    width: '100px',
                    sortable: false,
                    doNotRenderAsArray: true
                },
                comments: {
                    renderer: ProjectPapersRenderers.comments(rowData => this._getCommentsDialogData(rowData), isReadonly),
                    filterExpr: paperNote => paperNote.description,
                    sortable: false,
                    doNotRenderAsArray: true
                },
                color: {
                    header: $localize`Color`,
                    width: '120px',
                    renderer: isReadonly
                        ? null
                        : ExternalRenderers.dropdown(colorList$, (x: ApiColor) => x.name, 'value', 'name', null, true, $localize`No color`),
                    editable: isReadonly
                        ? null
                        : {
                            callback: (_, rowData) => this._projectStateService.firstSwitchMap(project =>
                                rowData.color
                                    ? this._papersClient.assignColor(project.id, rowData.id, rowData.color.name)
                                    : this._papersClient.unassignColor(project.id, rowData.id))
                                .pipe(switchMap(() => this._refreshRow(rowData))),
                            reloadRequired: false
                        },
                    sortable: false,
                    useBlankFilterItem: true,
                    filter: Filters.multiselect(colorList$.pipe(map(x => x.map(k => ({ id: k.name, name: k.name }))))),
                    filterExpr: x => x.name,
                    displayExpr: x => x.name
                }
            },
            defaultSort: [
                { field: x => x.isRejected, order: 'desc' },
                { field: x => x.isEditorial, order: 'desc' },
                { field: x => x.chapter.pos, order: 'asc' },
                { field: x => x.color.name, order: 'asc' }
            ]
        });
    }

    private _getDataSource(oDataQuery: string): Observable<PageResult<ApiPaperListItem>> {
        return combineLatest([this._projectStateService.model$, this._memberId$])
            .pipe(
                switchMap(([project, memberId]) => this._papersClient.getList(project.id, memberId, oDataQuery)),
                map(pageResult => {
                    pageResult.items = this._preserveOrder(pageResult.items);
                    let rowNumber = 1;
                    pageResult.items = pageResult.items.map(item => {
                        item.rowNumber = rowNumber++;
                        if (item.isRejected && item.chapter == null) {
                            item.chapter = this._rejectChapterItem;
                        }
                        return item;
                    });
                    return pageResult;
                }));
    }

    private _getCommentsDialogData(rowData: ApiPaperListItem): ICommentsData {

        const paperId = rowData.id;

        return {
            noteType: NoteTypeEnum.Comment,
            notesCountChanged$: this._refreshTable$,
            addCommentAction: model => this._projectStateService.firstSwitchMap(project => this._paperNotesClient.create(project.id, paperId, model)),
            comments$: this._projectStateService.switchMap(
                project => this._paperNotesClient.list(project.id, paperId)
                    .pipe(
                        map(comments => comments.map(comment =>
                        ({
                            comment: comment,
                            updateAction: (commentId: number, model: ApiNote) => this._paperNotesClient.update(project.id, paperId, commentId, model),
                            deleteAction: (commentId: number) => this._paperNotesClient.delete(project.id, paperId, commentId)
                        })))
                    ))
        };
    }
}
